import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ListEnd, SquarePen, TextAlignJustify, UserIcon } from "lucide-react";
import {
  openCreatePicker,
  useCanCreate,
} from "@components/CreateOptionPickerCst/host";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { Box, Typography } from "@mui/material";
import SectionWrapper from "./SectionWrapper";
import SectionHeader from "./SectionHeader";
import UserGroupsTabs from "./UsersGroupsTabs";
import UsersPanel from "./UsersPanel";
import GroupsPanel from "./GroupsPanel";
import LatestTrainingOfferings from "./LatestTrainingOfferings";
import {
  PERMISSIONS,
  canSeeTrainingOffers,
  canSeeUsersGroups,
  usePermissions,
} from "@utils/permissions";
import {
  SIDEBAR_MY_SPACE_DESCRIPTION_KEY,
  SIDEBAR_MY_SPACE_TITLE_KEY,
  SIDEBAR_NAMESPACE,
} from "@utils/utilsSidebarLabels";
import {
  getMySpaceLatestUsersGroupsSubtitleKey,
  getMySpaceLatestUsersGroupsTitleKey,
  getUsersGroupsReadPermissions,
} from "@utils/utilsUsersGroupsLabels";
import { useAuth } from "@context/auth";

export default function MySpace() {
  const { t } = useTranslation("my-space");
  // S04 / S05: i bottoni Crea sono disabilitati se l'utente non può creare
  // nessun elemento (prima il picker si apriva e si chiudeva senza messaggio).
  const canCreateCatalog = useCanCreate("catalog");
  const canCreateUsersGroups = useCanCreate("users-groups");
  const to = useNavigate();
  const { id: corporateId = "" } = useParams();

  // S04 (regola S05: sezioni nascoste senza permesso): ogni riquadro richiede
  // gli stessi permessi della pagina aperta da "Vedi tutti", così i dati non
  // vengono caricati e il link non porta a 403.
  const { can } = usePermissions();
  const { user } = useAuth();
  const showTrainingOffers = canSeeTrainingOffers(user?.permissions);
  const usersGroupsRead = getUsersGroupsReadPermissions(user?.permissions);
  const usersGroupsTabs = [
    {
      id: "users",
      permission: PERMISSIONS.users.read,
      label: t("latest-users-and-groups.tabs.users"),
      icon: <UserIcon />,
      panel: <UsersPanel />,
    },
    {
      id: "static-groups",
      permission: PERMISSIONS.groups.static.read,
      label: t("latest-users-and-groups.tabs.static-groups"),
      icon: <TextAlignJustify />,
      panel: <GroupsPanel tab="static-groups" />,
    },
    {
      id: "dynamic-groups",
      permission: PERMISSIONS.groups.dynamic.read,
      label: t("latest-users-and-groups.tabs.dynamic-groups"),
      icon: <ListEnd />,
      panel: <GroupsPanel tab="dynamic-groups" />,
    },
  ]
    .filter((tab) => can(tab.permission))
    .map(({ permission, ...tab }) => tab);
  const showUsersGroups = canSeeUsersGroups(user?.permissions);

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3, pr: 3 }}>
        <Typography
          sx={{
            fontSize: "28px",
            fontWeight: 700,
            color: (theme) => theme.palette.text.primary,
          }}
        >
          {t(SIDEBAR_MY_SPACE_TITLE_KEY, { ns: SIDEBAR_NAMESPACE })}
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: (theme) => theme.palette.text.primary,
            mt: 0.5,
          }}
        >
          {t(SIDEBAR_MY_SPACE_DESCRIPTION_KEY, { ns: SIDEBAR_NAMESPACE })}
        </Typography>
        {showTrainingOffers && (
          <SectionWrapper>
            <SectionHeader
              title={t("latest-training-offers.title")}
              subtitle={t("latest-training-offers.subtitle")}
              actions={[
                {
                  variant: "outlined",
                  label: t("header.see-all"),
                  onClick: () => {
                    to(`/v2/corporate/${corporateId}/detail/catalog`);
                  },
                },
                {
                  variant: "contained",
                  label: t("header.create"),
                  startIcon: <SquarePen size={14} />,
                  onClick: () => {
                    openCreatePicker("catalog");
                  },
                  disabled: !canCreateCatalog,
                },
              ]}
            />
            <LatestTrainingOfferings showDetailsButton={false} />
          </SectionWrapper>
        )}
        {showUsersGroups && (
          <SectionWrapper>
            <SectionHeader
              title={t(getMySpaceLatestUsersGroupsTitleKey(usersGroupsRead))}
              subtitle={t(
                getMySpaceLatestUsersGroupsSubtitleKey(usersGroupsRead),
              )}
              actions={[
                {
                  variant: "outlined",
                  label: t("header.see-all"),
                  onClick: () => {
                    to(`/v2/corporate/${corporateId}/detail/users-groups`);
                  },
                },
                {
                  variant: "contained",
                  label: t("header.create"),
                  startIcon: <SquarePen size={14} />,
                  onClick: () => {
                    openCreatePicker("users-groups");
                  },
                  disabled: !canCreateUsersGroups,
                },
              ]}
            />
            <UserGroupsTabs tabs={usersGroupsTabs} />
          </SectionWrapper>
        )}
      </Box>
    </AnimationFadeIn>
  );
}
